import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import Papa from "papaparse";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import DatePickerField from "../../common/DatePickerField";
import { useToast } from "../../../Moduels/Context/ToastContext";
import {
  addWfRosterAssignments,
  adminReopenRoster,
  approveWfRoster,
  archiveWfRoster,
  bulkCopyRosterAssignments,
  createWfRoster,
  deleteRosterAssignment,
  updateWfRoster,
  fetchRosterAssignmentAudit,
  fetchRosterAssignmentsGrid,
  fetchRosterStateLog,
  fetchWfHolidaysForRange,
  fetchWfRosters,
  fetchWfShifts,
  freezeWfRoster,
  publishWfRoster,
  searchEmployees,
  submitWfRoster,
  updateRosterAssignment,
  validateRosterPlan,
} from "../../../Moduels/attendance/rosterApi";
import {
  canEditRoster,
  canEditRosterMetadata,
  dedupeRostersByMonth,
  rosterStatusLabel,
} from "../../../Moduels/attendance/rosterValidation";
import {
  buildAssignmentRow,
  fieldErrorsFromDetail,
  isAdminRole,
  rosterApiErrorDetail,
  rosterApiErrorMessage,
  validateAssignmentSubmit,
  validationErrorFromCheck,
} from "../../../Moduels/attendance/rosterAssignmentUtils";
import RosterInlineAlert, { RosterFieldError } from "../../roster/RosterInlineAlert";
import AdminOverrideModal from "../../roster/AdminOverrideModal";
import AssignmentEditModal from "../../roster/AssignmentEditModal";
import AssignmentGrid from "../../roster/AssignmentGrid";
import BulkAssignPanel from "../../roster/BulkAssignPanel";
import RosterActivityTimeline from "../../roster/RosterActivityTimeline";
import RosterCalendarPlanner from "../../roster/RosterCalendarPlanner";
import RosterCreateModal from "../../roster/RosterCreateModal";
import ShiftPreviewCard from "../../roster/ShiftPreviewCard";
import WfEmployeeSelect from "../../roster/WfEmployeeSelect";
import WfShiftSelect from "../../roster/WfShiftSelect";
import { rosterTheme as t } from "../../roster/rosterTheme";
import RosterPlannerErrorBoundary from "../../roster/RosterPlannerErrorBoundary";
import { asArray } from "../../../Moduels/attendance/rosterSafeArrays";

const STATUS_ACTIONS = {
  draft: [{ key: "submit", label: "Submit for approval", fn: "submit" }],
  pending_approval: [{ key: "approve", label: "Approve", fn: "approve", adminOnly: true }],
  approved: [{ key: "publish", label: "Publish", fn: "publish" }],
  published: [{ key: "freeze", label: "Freeze", fn: "freeze", adminOnly: true }],
  frozen: [{ key: "archive", label: "Archive", fn: "archive", adminOnly: true }],
};

function WfRosterBoardInner() {
  const { role } = useContext(AuthContext);
  const toast = useToast();
  const isAdmin = isAdminRole(role);

  const [rosters, setRosters] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stateLog, setStateLog] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [rosterValidation, setRosterValidation] = useState(null);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const [rosterModal, setRosterModal] = useState({ open: false, edit: null });
  const [editModal, setEditModal] = useState({ open: false, assignment: null, cell: null });
  const [adminModal, setAdminModal] = useState(false);

  const [quickAssign, setQuickAssign] = useState({
    employee_id: "",
    work_date: "",
    shift_id: "",
    notes: "",
    override_reason: "",
  });
  const [assignError, setAssignError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const showAssignError = useCallback(
    (detail) => {
      setAssignError(detail);
      setFieldErrors(fieldErrorsFromDetail(detail));
      toast.error(detail.message);
    },
    [toast]
  );

  const clearAssignError = useCallback(() => {
    setAssignError(null);
    setFieldErrors({});
  }, []);

  const displayRosters = useMemo(() => dedupeRostersByMonth(rosters), [rosters]);

  const selectedRoster = useMemo(
    () => (rosters || []).find((r) => r.roster_plan_id === selectedId),
    [rosters, selectedId]
  );

  const lifecycleActions = useMemo(() => {
    const base = selectedRoster ? STATUS_ACTIONS[selectedRoster.status] : [];
    return asArray(base).filter((a) => !a.adminOnly || isAdmin);
  }, [selectedRoster, isAdmin]);

  const validationIssues = useMemo(() => {
    if (!rosterValidation) return [];
    return [...asArray(rosterValidation.errors), ...asArray(rosterValidation.warnings)];
  }, [rosterValidation]);

  const canEdit = canEditRoster(selectedRoster, isAdmin);
  const requireOverride =
    selectedRoster?.status === "published" || selectedRoster?.status === "frozen";
  const adminOverrideFlag = Boolean(requireOverride && isAdmin);

  const submitAssignments = async (rows, { overrideReason, silent } = {}) => {
    if (!selectedId || !selectedRoster) {
      const detail = {
        code: "ROSTER_REQUIRED",
        title: "Roster required",
        message: "Select a roster plan first.",
        field: null,
      };
      if (!silent) showAssignError(detail);
      return false;
    }

    const list = (Array.isArray(rows) ? rows : [rows]).map((r) =>
      buildAssignmentRow({
        ...r,
        override_reason: r.override_reason ?? overrideReason,
      })
    );

    for (const row of list) {
      const check = validateAssignmentSubmit({
        roster: selectedRoster,
        canEdit,
        isAdmin,
        requireOverride,
        employee_id: row.employee_id,
        work_date: row.work_date,
        shift_id: row.shift_id,
        override_reason: row.override_reason,
      });
      if (!check.ok) {
        if (!silent) showAssignError(validationErrorFromCheck(check));
        return false;
      }
    }

    try {
      await addWfRosterAssignments(selectedId, list, adminOverrideFlag);
      clearAssignError();
      return true;
    } catch (e) {
      if (!silent) showAssignError(rosterApiErrorDetail(e));
      return false;
    }
  };

  const loadRosters = useCallback(async () => {
    try {
      setRosters(asArray(await fetchWfRosters()));
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
      setRosters([]);
    }
  }, [toast]);

  const loadShifts = useCallback(async () => {
    try {
      setShifts(asArray(await fetchWfShifts()));
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
      setShifts([]);
    }
  }, [toast]);

  const loadAssignments = useCallback(async () => {
    if (!selectedId) {
      setAssignments([]);
      return;
    }
    setLoadingGrid(true);
    try {
      setAssignments(asArray(await fetchRosterAssignmentsGrid(selectedId)));
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
      setAssignments([]);
    } finally {
      setLoadingGrid(false);
    }
  }, [selectedId, toast]);

  const loadMeta = useCallback(async () => {
    if (!selectedRoster) return;
    try {
      const [hol, empRes, st, au, validation] = await Promise.all([
        fetchWfHolidaysForRange(selectedRoster.period_start, selectedRoster.period_end),
        searchEmployees({ page: 1, pageSize: 200, activeOnly: true }),
        fetchRosterStateLog(selectedId),
        fetchRosterAssignmentAudit(selectedId),
        validateRosterPlan(selectedId).catch(() => null),
      ]);
      setHolidays(asArray(hol));
      setEmployees(asArray(empRes?.items ?? empRes));
      setStateLog(asArray(st));
      setAuditLog(asArray(au));
      setRosterValidation(validation);
    } catch {
      setHolidays([]);
      setEmployees([]);
      setStateLog([]);
      setAuditLog([]);
    }
  }, [selectedId, selectedRoster]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInitial(true);
      await Promise.all([loadRosters(), loadShifts()]);
      if (!cancelled) setLoadingInitial(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRosters, loadShifts]);

  useEffect(() => {
    loadAssignments();
    loadMeta();
  }, [loadAssignments, loadMeta]);

  useEffect(() => {
    if (!selectedRoster) return;
    setQuickAssign((prev) => ({
      ...prev,
      work_date: prev.work_date || selectedRoster.period_start || "",
    }));
    clearAssignError();
  }, [selectedId, selectedRoster, clearAssignError]);

  const refreshAll = async () => {
    await loadRosters();
    await loadAssignments();
    await loadMeta();
  };

  const handleCreateRoster = async (payload) => {
    try {
      const r = await createWfRoster(payload);
      setSelectedId(r.roster_plan_id);
      toast.success("Roster plan created.");
      await loadRosters();
    } catch (e) {
      const detail = rosterApiErrorDetail(e);
      toast.error(detail.message);
      throw e;
    }
  };

  const handleUpdateRoster = async (rosterPlanId, payload) => {
    try {
      await updateWfRoster(rosterPlanId, payload);
      toast.success("Roster updated.");
      await loadRosters();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
      throw e;
    }
  };

  const handleOpenExistingRoster = (roster, { edit = false } = {}) => {
    setSelectedId(roster.roster_plan_id);
    setRosterModal(edit ? { open: true, edit: roster } : { open: false, edit: null });
  };

  const handleQuickAdd = async () => {
    const row = buildAssignmentRow(quickAssign);
    const check = validateAssignmentSubmit({
      roster: selectedRoster,
      canEdit,
      isAdmin,
      requireOverride,
      employee_id: row.employee_id,
      work_date: row.work_date,
      shift_id: row.shift_id,
      override_reason: row.override_reason,
    });
    if (!check.ok) {
      showAssignError(validationErrorFromCheck(check));
      return;
    }
    const ok = await submitAssignments([row]);
    if (!ok) return;
    toast.success("Assignment added.");
    setQuickAssign({
      employee_id: "",
      work_date: "",
      shift_id: "",
      notes: "",
      override_reason: "",
    });
    await refreshAll();
  };

  const handleSaveAssignment = async (form) => {
    const row = buildAssignmentRow(form);
    if (editModal.assignment?.assignment_id) {
      try {
        await updateRosterAssignment(selectedId, editModal.assignment.assignment_id, {
          ...row,
          admin_override: adminOverrideFlag,
        });
        toast.success("Assignment updated.");
        await refreshAll();
      } catch (e) {
        toast.error(rosterApiErrorMessage(e));
        throw e;
      }
      return;
    }

    const check = validateAssignmentSubmit({
      roster: selectedRoster,
      canEdit,
      isAdmin,
      requireOverride,
      employee_id: row.employee_id,
      work_date: row.work_date,
      shift_id: row.shift_id,
      override_reason: row.override_reason,
    });
    if (!check.ok) {
      toast.error(check.message);
      throw new Error(check.message);
    }
    const ok = await submitAssignments([row]);
    if (!ok) throw new Error("Assignment create failed");
    toast.success("Assignment created.");
    await refreshAll();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete assignment for ${row.employee_name} on ${row.work_date}?`)) return;
    try {
      await deleteRosterAssignment(selectedId, row.assignment_id, adminOverrideFlag);
      toast.success("Assignment removed.");
      await refreshAll();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    }
  };

  const handleDuplicate = async (row) => {
    const next = new Date(row.work_date + "T00:00:00");
    next.setDate(next.getDate() + 1);
    const nd = next.toISOString().slice(0, 10);
    if (nd > selectedRoster.period_end) {
      toast.error("Cannot duplicate outside roster period.");
      return;
    }
    const ok = await submitAssignments([
      buildAssignmentRow({
        employee_id: row.employee_id,
        work_date: nd,
        shift_id: row.shift_id,
        notes: row.notes,
      }),
    ]);
    if (!ok) return;
    toast.success("Duplicated to next day.");
    await refreshAll();
  };

  const handleCopyWeek = async (row) => {
    const d = new Date(row.work_date + "T00:00:00");
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((day + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const target = new Date(sunday);
    target.setDate(sunday.getDate() + 1);
    try {
      await bulkCopyRosterAssignments(selectedId, {
        source_from: monday.toISOString().slice(0, 10),
        source_to: sunday.toISOString().slice(0, 10),
        target_from: target.toISOString().slice(0, 10),
        employee_ids: [row.employee_id],
        admin_override: adminOverrideFlag,
      });
      toast.success("Week copied forward.");
      await refreshAll();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    }
  };

  const handleBulkAssign = async (list) => {
    const normalized = list.map((r) => buildAssignmentRow(r));
    const ok = await submitAssignments(normalized);
    if (!ok) return;
    toast.success(`${normalized.length} assignments created.`);
    await refreshAll();
  };

  const handleBulkCopy = async (payload) => {
    try {
      const res = await bulkCopyRosterAssignments(selectedId, {
        ...payload,
        admin_override: adminOverrideFlag,
      });
      toast.success(`Copied ${res.created} assignments.`);
      await refreshAll();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    }
  };

  const handleImportCsv = (file) => {
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data
          .map((r) => ({
            employee_id: r.employee_id || r.EmployeeId,
            work_date: r.work_date || r.date || r.Date,
            shift_id: r.shift_id || r.ShiftId,
            notes: r.notes || "",
          }))
          .filter((r) => r.employee_id && r.work_date);
        if (!rows.length) {
          toast.error("No valid rows in CSV (need employee_id, work_date).");
          return;
        }
        await handleBulkAssign(rows);
      },
    });
  };

  const handleCellClick = (employeeId, workDate, existing) => {
    if (!canEdit) return;
    if (existing) {
      setEditModal({ open: true, assignment: existing, cell: null });
    } else {
      setEditModal({
        open: true,
        assignment: null,
        cell: { employee_id: employeeId, work_date: workDate },
      });
    }
  };

  const handleFillRange = (employeeId, from, to) => {
    if (!canEdit) return;
    setEditModal({
      open: true,
      assignment: null,
      cell: { employee_id: employeeId, work_date: from, range_to: to },
    });
  };

  const runLifecycle = async (fn) => {
    if (!selectedId) return;
    setLoadingRoster(true);
    try {
      if (fn === "submit") await submitWfRoster(selectedId);
      else if (fn === "approve") await approveWfRoster(selectedId);
      else if (fn === "publish") await publishWfRoster(selectedId);
      else if (fn === "freeze") await freezeWfRoster(selectedId);
      else if (fn === "archive") await archiveWfRoster(selectedId);
      toast.success(`Roster ${fn} completed.`);
      await refreshAll();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    } finally {
      setLoadingRoster(false);
    }
  };

  const handleAdminOverride = async ({ action, override_reason }) => {
    try {
      if (action === "reopen") {
        await adminReopenRoster(selectedId, override_reason);
        toast.success("Roster reopened for editing.");
      } else if (action === "force_publish") {
        await publishWfRoster(selectedId);
        toast.success("Roster published.");
      }
      await refreshAll();
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    }
  };

  const selectedShift = (shifts || []).find((s) => s.shift_id === quickAssign.shift_id);
  const editAssignment = editModal.assignment
    ? editModal.assignment
    : editModal.cell
      ? {
          employee_id: editModal.cell.employee_id,
          work_date: editModal.cell.work_date,
          shift_id: "",
          notes: "",
        }
      : null;

  if (loadingInitial) {
    return (
      <div className={t.page}>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-200 rounded-lg w-1/3" />
          <div className="h-32 bg-slate-100 rounded-xl" />
          <div className="h-64 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className={t.page}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/attendance/roster/guide"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-900 mb-2"
          >
            <BookOpen size={16} />
            Roster setup guide
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900">Roster planner</h1>
          <p className={t.cardSub + " mt-1"}>
            Shift scheduling, approvals, and monthly planning
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setRosterModal({ open: true, edit: null })}
            className={t.btnPrimary}
          >
            + New roster
          </button>
          {isAdmin && selectedRoster && ["published", "frozen", "approved"].includes(selectedRoster.status) && (
            <button
              type="button"
              onClick={() => setAdminModal(true)}
              className="px-4 py-2 text-sm font-medium border border-amber-200 text-amber-900 rounded-lg bg-amber-50 hover:bg-amber-100"
            >
              Admin override
            </button>
          )}
        </div>
      </header>

      {rosterValidation && (rosterValidation.error_count > 0 || rosterValidation.warning_count > 0) && (
        <div
          className={
            "rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          }
        >
          <strong>
            Roster validation: {rosterValidation.error_count} error(s), {rosterValidation.warning_count}{" "}
            warning(s)
          </strong>
          <ul className="mt-2 space-y-1 list-disc list-inside max-h-32 overflow-y-auto">
            {validationIssues.slice(0, 12).map((w, i) => (
                <li key={`${w.code}-${i}`}>
                  {w.employee_name || w.employee_code || "Employee"}: {w.message}
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className={t.cardPad}>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className={t.label}>Roster plan</label>
            <select
              className={t.select}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">Select roster…</option>
              {displayRosters.map((r) => (
                <option key={r.roster_plan_id} value={r.roster_plan_id}>
                  {r.name} · {r.period_start} → {r.period_end}
                </option>
              ))}
            </select>
          </div>
          {selectedRoster && canEditRosterMetadata(selectedRoster) && (
            <button
              type="button"
              onClick={() => setRosterModal({ open: true, edit: selectedRoster })}
              className={t.btnSecondary}
            >
              Edit roster
            </button>
          )}
          {selectedRoster && (
            <>
              <span
                className={`text-xs font-medium px-3 py-1.5 rounded-full ${t.badge(
                  selectedRoster.status
                )}`}
              >
                {rosterStatusLabel(selectedRoster.status)} · v{selectedRoster.version}
              </span>
              <div className="flex flex-wrap gap-2">
                {lifecycleActions.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    disabled={loadingRoster}
                    onClick={() => runLifecycle(a.fn)}
                    className={`${t.btnPrimary} !py-1.5 !px-3`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedRoster && assignError && (
        <RosterInlineAlert error={assignError} onDismiss={clearAssignError} />
      )}

      {selectedRoster && canEdit && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className={`lg:col-span-2 ${t.cardPad} space-y-3`}>
            <h2 className={t.cardHeader}>Quick assignment</h2>
            <div>
              <label className={t.label}>Employee *</label>
              <div
                className={
                  fieldErrors.employee_id ? "rounded-lg ring-2 ring-red-400 ring-offset-1" : ""
                }
              >
                <WfEmployeeSelect
                  value={quickAssign.employee_id}
                  onChange={(id) => {
                    setQuickAssign({ ...quickAssign, employee_id: id });
                    if (fieldErrors.employee_id) clearAssignError();
                  }}
                />
              </div>
              <RosterFieldError message={fieldErrors.employee_id} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <DatePickerField
                  label="Work date"
                  required
                  value={quickAssign.work_date}
                  min={selectedRoster.period_start}
                  max={selectedRoster.period_end}
                  inputClassName={`${t.input}${fieldErrors.work_date ? " border-red-400 ring-1 ring-red-300" : ""}`}
                  onChange={(date) => {
                    setQuickAssign({ ...quickAssign, work_date: date });
                    if (fieldErrors.work_date) clearAssignError();
                  }}
                />
                <RosterFieldError message={fieldErrors.work_date} />
              </div>
              <div>
                <label className={t.label}>Shift *</label>
                <div
                  className={
                    fieldErrors.shift_id ? "rounded-lg ring-2 ring-red-400 ring-offset-1" : ""
                  }
                >
                  <WfShiftSelect
                    shifts={shifts}
                    value={quickAssign.shift_id}
                    onChange={(id) => {
                      setQuickAssign({ ...quickAssign, shift_id: id });
                      if (fieldErrors.shift_id) clearAssignError();
                    }}
                  />
                </div>
                <RosterFieldError message={fieldErrors.shift_id} />
              </div>
            </div>
            <input
              className={t.input}
              placeholder="Notes (optional)"
              value={quickAssign.notes}
              onChange={(e) => setQuickAssign({ ...quickAssign, notes: e.target.value })}
            />
            {requireOverride && isAdmin && (
              <div>
                <label className={t.label}>Override reason *</label>
                <textarea
                  className={`${t.input} min-h-[72px]${fieldErrors.override_reason ? " border-red-400 ring-1 ring-red-300" : ""}`}
                  placeholder="Required when editing a published roster"
                  value={quickAssign.override_reason}
                  onChange={(e) => {
                    setQuickAssign({ ...quickAssign, override_reason: e.target.value });
                    if (fieldErrors.override_reason) clearAssignError();
                  }}
                />
                <RosterFieldError message={fieldErrors.override_reason} />
              </div>
            )}
            <button type="button" onClick={handleQuickAdd} className={`w-full ${t.btnPrimary} !py-2.5`}>
              Add assignment
            </button>
          </div>
          <ShiftPreviewCard shift={selectedShift} />
        </div>
      )}

      {selectedRoster && (
        <>
          <RosterCalendarPlanner
            roster={selectedRoster}
            assignments={assignments}
            employees={employees}
            shifts={shifts}
            holidays={holidays}
            canEdit={canEdit}
            onCellClick={handleCellClick}
            onFillRange={handleFillRange}
          />

          {canEdit && (
            <BulkAssignPanel
              roster={selectedRoster}
              shifts={shifts}
              canEdit={canEdit}
              onBulkAssign={handleBulkAssign}
              onBulkCopy={handleBulkCopy}
              onImportCsv={handleImportCsv}
              onAssignError={showAssignError}
            />
          )}

          <AssignmentGrid
            rows={assignments}
            loading={loadingGrid}
            canEdit={canEdit}
            isAdmin={isAdmin}
            onEdit={(r) => setEditModal({ open: true, assignment: r, cell: null })}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onCopyWeek={handleCopyWeek}
          />

          <div className="grid lg:grid-cols-2 gap-4">
            <RosterActivityTimeline
              stateLog={stateLog}
              assignmentAudit={auditLog}
              loading={loadingGrid}
            />
            <div className={`${t.cardPad} text-sm text-slate-600`}>
              <h3 className={t.cardHeader + " mb-2"}>Period</h3>
              <p>
                {selectedRoster.period_start} → {selectedRoster.period_end}
              </p>
              {selectedRoster.timezone && (
                <p className="mt-1">Timezone: {selectedRoster.timezone}</p>
              )}
              {selectedRoster.unit_label && (
                <p className="mt-1">Unit: {selectedRoster.unit_label}</p>
              )}
              {!canEdit && (
                <p className="mt-3 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2">
                  This roster is read-only. {isAdmin ? "Use Admin override to edit." : "Contact HR admin."}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {!selectedRoster && displayRosters.length === 0 && (
        <div className={`${t.card} text-center py-16 text-slate-500 bg-slate-50/50`}>
          <p>No roster plans yet.</p>
          <button
            type="button"
            onClick={() => setRosterModal({ open: true, edit: null })}
            className={`mt-3 ${t.btnGhost}`}
          >
            Create your first monthly roster
          </button>
        </div>
      )}

      <RosterCreateModal
        open={rosterModal.open}
        editRoster={rosterModal.edit}
        rosters={rosters}
        onClose={() => setRosterModal({ open: false, edit: null })}
        onCreate={handleCreateRoster}
        onUpdate={handleUpdateRoster}
        onOpenExisting={handleOpenExistingRoster}
      />

      <AssignmentEditModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, assignment: null, cell: null })}
        assignment={editAssignment}
        roster={selectedRoster}
        shifts={shifts}
        existing={assignments}
        readOnly={!canEdit}
        requireOverride={requireOverride && isAdmin}
        onSave={handleSaveAssignment}
      />

      <AdminOverrideModal
        open={adminModal}
        onClose={() => setAdminModal(false)}
        roster={selectedRoster}
        onConfirm={handleAdminOverride}
      />
    </div>
  );
}

export default function WfRosterBoard() {
  return (
    <RosterPlannerErrorBoundary>
      <WfRosterBoardInner />
    </RosterPlannerErrorBoundary>
  );
}
